import { useInfiniteQuery, type InfiniteData } from '@tanstack/react-query';
import {
  listConversations,
  type ConversationFilter,
  type ConversationsPage,
} from '@/lib/api';

interface UseConversationsArgs {
  slug: string;
  filter: ConversationFilter;
  search: string;
  pageSize?: number;
}

const DEFAULT_PAGE_SIZE = 50;

export function useConversations({
  slug,
  filter,
  search,
  pageSize = DEFAULT_PAGE_SIZE,
}: UseConversationsArgs) {
  return useInfiniteQuery<
    ConversationsPage,
    Error,
    InfiniteData<ConversationsPage, number>,
    readonly [string, string, ConversationFilter, string, number],
    number
  >({
    queryKey: ['conversations', slug, filter, search, pageSize] as const,
    enabled: Boolean(slug),
    initialPageParam: 0,
    queryFn: ({ pageParam }) =>
      listConversations({
        slug,
        filter,
        search,
        limit: pageSize,
        offset: pageParam,
      }),
    getNextPageParam: (lastPage) => lastPage.next_offset,
    staleTime: 15_000,
    // 2026-05-26 fix: refetch on window focus + reconnect so that when the
    // owner archives several conversations and then closes/backgrounds the
    // PWA (Mac standalone or iOS), reopening immediately pulls fresh data
    // instead of showing the stale pre-archive list. Without this, the
    // global default (refetchOnWindowFocus: false from main.tsx) meant
    // archived rows reappeared in Needs You on reopen and only disappeared
    // after a manual reload — see Ernie report 2026-05-26.
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  });
}
